/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './EfficiencyTrendChart.css';
import { IconWithTooltip } from '../../globalUtils/globalIcons';
import { SearchBox, SelectDropDown, useAppDispatch, useAppSelector, useEffectOnce } from '../../globalUtils/globalExports';
import { getDashboardReducer, getReferenceDataReducer, setFilterData } from '../../rmsReduxStore/reduxExports';
import { format, parse, isValid } from 'date-fns';
import { RectangularSkeleton } from '../../globalUtils/SkeletonDesign/SkeletonDesign';
import CustomerList from './CustomerList';

interface EfficiencyTrendProp {
    handleFilter: Function,
    getFilterValue: Function
}

const EfficiencyTrendChart: React.FC<EfficiencyTrendProp> = (props) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const { handleFilter, getFilterValue } = props;
    const { intervalRangeReferenceData } = useAppSelector(getReferenceDataReducer);
    const [filterValue, setFilterValue] = useState<IGeneralReferenceData>()
    const [searchTerm, setSearchTerm] = useState<string | null>(null)
    const dispatch = useAppDispatch();
    const navigationUpperLimit = 31
    const { newDashboardEfficiencyTrendInfo, newDashboardCustomerAverageEfficiency } = useAppSelector(getDashboardReducer);
    const [visibleMin, setVisibleMin] = useState(0);
    const [visibleMax, setVisibleMax] = useState(navigationUpperLimit);
    const dataLength = newDashboardEfficiencyTrendInfo?.length || 0;

    useEffectOnce(() => {
        setFilterValue(getFilterValue('energyEfficiencyRange'))
    })

    const hasCustomerData = newDashboardEfficiencyTrendInfo?.some(entry =>
        Array.isArray(entry.customerWiseData) && entry.customerWiseData.length > 0
    );

    const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
        const rawValue = this.value;
        const label = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
        const [day, month] = label.split(' ');

        const chartWidth = this.axis.chart.chartWidth;
        const numCategories = this.axis.categories?.length || 1;
        const approxCategoryWidth = chartWidth / numCategories;

        const shouldWrap = approxCategoryWidth > 0; // adjust threshold if needed
        // const shouldWrap = true

        return shouldWrap
            ? `<div style="text-align: center;">${day}<br>${month}</div>`
            : `<div style="text-align: center;">${day}<br><span style="opacity: 0.6; font-size: 10px;">${month}</span></div>`;
    };

    const getFormattedData = useCallback(() => {
        const rawData = newDashboardEfficiencyTrendInfo || [];

        const slicedData = rawData.slice(visibleMin, visibleMax + 1);
        

        const xAxis = slicedData.map(item => formatTimeStamp(item.timeStamp));

        const customerNamesSet = new Set<string>();
        (newDashboardEfficiencyTrendInfo || []).forEach(item => {
            item.customerWiseData?.forEach(customer => {
                customerNamesSet.add(customer.name);
            });
        });

        function formatTimeStamp(rawTimeStamp: string): string {
            let dateObj;

            const timeMatch = rawTimeStamp.match(/^(\d{1,2})(am|pm)$/i);
            if (timeMatch) {
                const hours = parseInt(timeMatch[1], 10);
                const isPM = timeMatch[2].toLowerCase() === 'pm';
                const hour24 = isPM ? (hours === 12 ? 12 : hours + 12) : (hours === 12 ? 0 : hours);
                dateObj = new Date();
                dateObj.setHours(hour24, 0, 0, 0);
                return format(dateObj, 'h a'); // e.g. "9 AM"
            }

            // Try ISO date first (like '2025-05-28')
            dateObj = new Date(rawTimeStamp);
            if (isValid(dateObj)) {
                return format(dateObj, 'd MMM'); // e.g. "28 May"
            }

            // If not ISO, try 'd MMM' with current year
            const currentYear = new Date().getFullYear();
            dateObj = parse(`${rawTimeStamp} ${currentYear}`, 'd MMM yyyy', new Date());
            if (isValid(dateObj)) {
                return format(dateObj, 'd MMM'); // e.g. "23 Apr"
            }
            return rawTimeStamp;
        }

        const customerNames = Array.from(customerNamesSet);

        const series = customerNames.map(name => {
            return {
                name,
                type: 'line',
                data: slicedData.map(item => {
                    const match = item.customerWiseData?.find(c => c.name === name);
                    return match ? match.efficiency : 0;
                }),
                marker: false,
            };
        });

        return {
            xAxis,
            series,
        };
    }, [newDashboardEfficiencyTrendInfo, visibleMin, visibleMax]);

    let labelStep = 1;  //draft
    const { xAxis } = getFormattedData();
    const totalBars = xAxis.length;
    if (totalBars > 20) labelStep = 4;
    else if (totalBars > 10) labelStep = 2;

    const { series } = getFormattedData();
    const chartHeight = 400;

    const chartOptions: Highcharts.Options = {
        chart: {
            type: 'line',
            height: chartHeight,
            spacingTop: 10,
            spacingRight: 10,
            spacingBottom: 0,
            spacingLeft: 10,
            events: {
                render: function () {
                    const chart = this as Highcharts.Chart & any;

                    // Remove existing custom arrows before adding new ones
                    const existingArrows = chart.container.getElementsByClassName('custom-arrow');
                    while (existingArrows.length > 0) {
                        existingArrows[0].remove();
                    }

                    // Left arrow (shifted more inside by changing left position)
                    if (visibleMin > 0) {
                        chart.renderer.image(
                            '/images/left_arrow_highchart.svg',
                            chart.plotLeft, // shifted inside
                            chart.plotTop + chart.plotHeight / 2 - 10,
                            20,
                            20
                        )
                            .addClass('custom-arrow left')
                            .on('click', () => handleNavigation('left'))
                            .add();
                    }

                    // Right arrow (shifted more inside by changing left position)
                    if (visibleMax < dataLength - 1) {
                        chart.renderer.image(
                            '/images/right_arrow_highchart.svg',
                            chart.plotLeft + chart.plotWidth - 15, // shifted inside
                            chart.plotTop + chart.plotHeight / 2 - 10,
                            20,
                            20
                        )
                            .addClass('custom-arrow right')
                            .on('click', () => handleNavigation('right'))
                            .add();
                    }
                },
            },
        },
        title: { text: undefined },
        subtitle: { text: undefined },
        xAxis: {
            // title: {
            //   text: 'Month',
            // },
            categories: xAxis,
            lineColor: '#3347E6',
            tickLength: 5,
            gridLineWidth: 1,
            tickmarkPlacement: 'on',
            gridLineDashStyle: 'ShortDash',
            labels: {
                rotation: 0,
                useHTML: true,
                step: labelStep,
                formatter: xAxisLabelFormatter,
            }
        },
        yAxis: {
            gridLineColor: '#D3D6DF',
            gridLineWidth: 1,
            gridLineDashStyle: 'ShortDash',
            title: {
                text: 'Efficiency (%)'
            }
        },
        tooltip: {
            shared: false,
            valueDecimals: 2,
        },
        legend: {
            enabled: false
        },
        series: getFormattedData().series as Highcharts.SeriesOptionsType[],
        credits: { enabled: false },
        exporting: { enabled: false },
    };

    const handleSelectChange = (val): void => {
        setFilterValue(val);
        handleEfficiencyTrendFilter(val, 'energyEfficiencyRange');
        handleChartPageReset();
    };

    const handleChartPageReset = () => {
        setVisibleMin(0);
        setVisibleMax(navigationUpperLimit);
    }

    const handleNavigation = (direction: 'left' | 'right') => {
        const step = navigationUpperLimit + 1;
        if (direction === 'left') {
            setVisibleMin(Math.max(visibleMin - step, 0));
            setVisibleMax(Math.max(visibleMax - step, step));
        } else {
            setVisibleMin(Math.min(visibleMin + step, dataLength - (step + 1)));
            setVisibleMax(Math.min(visibleMax + step, dataLength - 1));
        }
    };

    const handleEfficiencyTrendFilter = useCallback(async (val: any, filterKey: string): Promise<void> => {
        const res = await setFilterData(val, filterKey, 'dashboardV2', 'SET', false)(dispatch)
        if (res?.message === 'Action dispatched successfully') {
            handleFilter(res?.filters);
        }
    }, [setFilterData])

    const customersForList = newDashboardCustomerAverageEfficiency?.map(customer => {
        const seriesIndex = series.findIndex(s => s.name === customer.name);
        const color = seriesIndex >= 0 ? (series[seriesIndex] as any).color || Highcharts.getOptions().colors?.[seriesIndex % 10] : '#999';
        return {
            ...customer,
            isSelected: true,
            seriesIndex,
            color
        };
    });

    const toggleCustomerSeries = (seriesIndex: number, isSelected: boolean) => {
        const chart = chartRef.current?.chart;
        if (!chart) return;

        const series = chart.series[seriesIndex];
        if (!series) return;

        chart.xAxis.forEach(axis => {
            axis.update({
                gridLineWidth: 1, // force grid lines to always appear
            }, false); // false = don't redraw yet
        });

        chart.yAxis.forEach(axis => {
            axis.update({
                labels: { enabled: true },
                gridLineWidth: 1,
            }, false);
        });

        series.setVisible(isSelected, false); // false -> don't redraw yet
        chart.redraw(); // redraw after updating visibility
    };

    const toggleAllCustomers = (value: boolean) => {
        const chart = chartRef.current?.chart;
        if (!chart) return;

        // Toggle all series visibility without redrawing each time
        chart.series.forEach(series => {
            series.setVisible(value, false); // false -> don't redraw yet
        });

        // Toggle yAxis grid lines only, keep labels and title visible
        chart.yAxis.forEach(axis => {
            axis.update(
                {
                    labels: { enabled: value }, // hide numeric labels
                    gridLineWidth: value ? 1 : 0,
                    min: axis.userOptions.min,
                    max: axis.userOptions.max
                },
                false // don't redraw yet
            );
        });

        chart.redraw(); // single redraw
    };


    /*
        Requirement change: No Last 24 Hour data needed to be shown on the graph hence filter is removed
    */

    const modifiedFilterRef = useMemo(()=>intervalRangeReferenceData?.filter(item=>item?.id !== 'last24hr'),[intervalRangeReferenceData])


    return (
        <div className='widget-container-efficiency'>
            <div className="efficiency-chart-card">
                <div className="widget-header">
                    <div className="widget-heading efficiency-trend-chart-header">
                        Charger Efficiency VS Time
                        <IconWithTooltip iconClassName='info-icon' toolTipText='Average Charger Efficiency per Customer' containerClassName={'active-alarm-tooltip'} />
                    </div>
                    <div className="chart-filters">
                        {/* <select disabled>
            <option>Select Stations</option>
          </select > */}
                        {/* <select disabled>
            <option>Monthly</option>
          </select> */}
                        <SelectDropDown
                            selectValue={filterValue}
                            selectOptions={modifiedFilterRef ?? []}
                            handleSelectOptionChange={handleSelectChange}
                            selectDropDownId='alarm-failed-transaction-dropdown'
                            dropDownClassName='alarm-failed-transaction-dropdown'
                            isMultiSelect={false}
                            placeholder={'Interval'}
                        />
                    </div>
                </div>
                <div className="widget-body">
                    <div className="highchart-wrap">
                        {!newDashboardEfficiencyTrendInfo ? (
                            <div style={{ height: `${chartHeight}px` }}>
                                <RectangularSkeleton skeletonContainerClassName='' />
                            </div>
                        ) : hasCustomerData ? (
                                <div className='highchart-container'>
                                    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                                </div>
                        ) : (
                            <div className="no-data-text">No Data Available</div>
                        )}
                    </div>
                </div>
            </div>
            <div className='customer-list-wrap'>
                <div className='customer__list__wrap__filter__header-search'>
                    <SearchBox
                        searchFieldId='efficiency-chart-search-box'
                        searchBoxClassName='efficiency__chart__search__box-wrap'
                        handleSearch={setSearchTerm}
                    />
                </div>
                <div className='customer__list__wrap__filter__body'>
                        <CustomerList customers={customersForList} onCheckboxChange={toggleCustomerSeries} onSelectAllChange={toggleAllCustomers} searchText={searchTerm} />
                </div>
            </div>
        </div>
    );
};

export default EfficiencyTrendChart;